/**
 * BriefingRoomServices — Module-Host M3a service-binding contract.
 *
 * The module SPA reads services from a single React context (see
 * contexts/BriefingRoomContext.tsx). Two concrete implementations of
 * every interface exist:
 *   - host/*ServiceModule.ts        when running inside vibe-designer
 *   - standalone/*ServiceStandalone.ts when running as a downloadable HTML page
 *
 * M3a wires the module-host variants for Voice/LLM/MCP/Auth + stubs
 * Persistence pending Engine E1's campaign routes. All 5 standalone
 * variants are ServiceUnavailableError stubs until M4 (degradation
 * matrix sprint).
 *
 * Source-of-truth — planner-track M3:
 *   docs/GAMES/RPG/007 JAMES BOND RPG/DOCS/
 *     20260531_BRIEFING_ROOM_PLAN_MODULE_HOST_TRACK.md §M3
 */

export type HostMode = 'module' | 'standalone';

export class ServiceUnavailableError extends Error {
  constructor(public readonly serviceName: string, public readonly reason: string) {
    super(`${serviceName} unavailable: ${reason}`);
    this.name = 'ServiceUnavailableError';
  }
}

// ─── Voice ──────────────────────────────────────────────────────────────

export type TranscriptListener = (text: string) => void;
export type UnsubscribeFn = () => void;

// V1 Voice GM (2026-06-02): TTS narration.
//
// V1 default voice = `bm_lewis` (British Male) — chosen as the cinematic
// "GM narrator" baseline. Engines registered server-side at
// vibe-designer/backend/routes/voice.py:1014-1126 — Kokoro (default),
// VibeVoice, qwen3-tts. Leaving `engine` undefined uses the server's
// default (Kokoro) per the route's _resolve_engine.
export interface SpeakOptions {
  voice?: string;
  engine?: string;
  signal?: AbortSignal;
}

// SpeakResult separates the network/synth concern (VoiceService.speak)
// from the playback concern (ReadAloudControl component). Service returns
// a blob URL + a revoke callback so the caller controls when the audio
// element unmounts AND when the URL is freed. Caller MUST call revoke()
// on unmount or the blob leaks. See ReadAloudControl for the canonical
// consumer.
export interface SpeakResult {
  audioUrl: string;
  revoke(): void;
}

export class TtsUnavailableError extends Error {
  constructor(public readonly reason: string) {
    super(`TTS unavailable: ${reason}`);
    this.name = 'TtsUnavailableError';
  }
}

// V2 Voice GM (2026-06-02) — STT session API.
//
// VoiceService.startStt() opens a streaming pipeline to vibe-designer's
// /api/voice/turn WebSocket (per routes/voice.py:603-865 — v19.2 protocol).
// The session handle controls the audio capture lifecycle on the client
// side; the server runs whisperx and emits a {type:'transcript'} frame
// when STT completes. V2 closes the WS after the transcript so the
// downstream LLM pipeline never runs (V3 will replace this with a path
// that returns the orchestrator's tool-call result).
export interface SttOptions {
  language?: string;
  engine?: string;
  mimeType?: string;
}

export interface SttSession {
  onPartial(cb: (text: string) => void): UnsubscribeFn;
  onFinal(cb: (text: string) => void): UnsubscribeFn;
  onError(cb: (err: Error) => void): UnsubscribeFn;
  stop(): Promise<string>;
  cancel(): void;
}

export class SttUnavailableError extends Error {
  constructor(public readonly reason: string) {
    super(`STT unavailable: ${reason}`);
    this.name = 'SttUnavailableError';
  }
}

export interface VoiceService {
  startListening(): Promise<void>;
  stopListening(): Promise<void>;
  onTranscript(cb: TranscriptListener): UnsubscribeFn;
  speak(text: string, opts?: SpeakOptions): Promise<SpeakResult>;
  startStt(opts?: SttOptions): Promise<SttSession>;
  isAvailable(): boolean;
}

// ─── Persistence (campaigns) ────────────────────────────────────────────

export interface CampaignMeta {
  id: string;
  name: string;
  module_id: string;
  created_at: string;
  updated_at: string;
}

export interface CampaignHandle {
  meta: CampaignMeta;
  close(): Promise<void>;
}

export interface PersistenceService {
  openCampaign(id: string): Promise<CampaignHandle>;
  listCampaigns(): Promise<CampaignMeta[]>;
  createCampaign(name: string, moduleId: string): Promise<CampaignMeta>;
  /**
   * Delete a campaign by id. 2026-06-07. Backend route returns 204 No
   * Content on success, 404 on unknown id, 422 on malformed id. The
   * implementation throws Error on non-2xx so the picker can surface a
   * toast / banner.
   */
  deleteCampaign(id: string): Promise<void>;
  isAvailable(): boolean;
}

// ─── LLM ────────────────────────────────────────────────────────────────

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface LlmService {
  chat(messages: ChatMessage[]): Promise<string>;
  isAvailable(): boolean;
}

// ─── GMOrchestrator (V3 Voice GM) ──────────────────────────────────────

// V3 Voice GM (2026-06-02) — GMOrchestrator dispatch.
//
// Frontend sends the player transcript + view/pregen context to
// POST /api/modules/briefing-room/dispatch. Server calls the per-tenant
// LLM router with the 7-tool schema (defined in briefing_room_backend/
// routes/dispatch.py:TOOLS) and returns one of:
//   - {action: {tool, args, rationale?}, reply_text, model}
//   - {action: null, reply_text, model}  (plain reply, no tool call)
//
// Tool names (string-typed instead of union for forward-compat with
// future tools — V4+ may add more):
//   pick_decision / roll_skill / describe_npc / look_around /
//   check_inventory / recall_briefing / ask_npc

export interface DispatchAction {
  tool: string;
  args: Record<string, unknown>;
  rationale?: string;
}

export interface DispatchView {
  kind: 'briefing' | 'paragraph' | 'ending';
  paragraph_number?: number;
  paragraph_text?: string;
  decision_labels: string[];
}

export interface DispatchHistoryTurn {
  role: 'player' | 'gm' | 'system';
  text: string;
}

export interface DispatchRollOutcome {
  skill: string;
  outcome: string;
}

export interface DispatchRequest {
  transcript: string;
  view: DispatchView;
  history: DispatchHistoryTurn[];
  pregen_name?: string;
  pregen_skills?: Record<string, number>;
  scenario_id?: string;
  state_history?: number[];
  state_rolls?: DispatchRollOutcome[];
  inventory?: string[];
}

export interface DispatchResponse {
  action: DispatchAction | null;
  reply_text: string;
  model: string | null;
}

export class GmOrchestratorError extends Error {
  constructor(
    public readonly code: string,
    public readonly httpStatus: number,
    message: string,
  ) {
    super(message);
    this.name = 'GmOrchestratorError';
  }
}

export interface GmOrchestratorService {
  dispatch(req: DispatchRequest): Promise<DispatchResponse>;
  isAvailable(): boolean;
}

// ─── MCP ────────────────────────────────────────────────────────────────

export interface McpService {
  query<T = unknown>(tool: string, args: Record<string, unknown>): Promise<T>;
  isAvailable(): boolean;
}

export interface McpRegistry {
  mind: McpService;
  sight: McpService;
  cortex: McpService;
}

// ─── Auth ───────────────────────────────────────────────────────────────

export interface AuthService {
  getTenantId(): string | null;
  getUserId(): string | null;
  isAuthenticated(): boolean;
}

// ─── On-screen context (see+control, 2026-07-01) ─────────────────────────

/**
 * A structured "what's on screen right now" snapshot the module PUSHES to the
 * host so the host's voice ("BRIDGE") and Pi agents can SEE the Briefing Room
 * (which campaign/character is open, what stage of play). Structurally matches
 * the host's SpaContext (the host casts it) — kept as a local type so the
 * module stays independent of the host package.
 */
export interface SpaContextSnapshot {
  view: string;
  title: string;
  detail: string;
  items: string[];
  moduleId?: string;
  activeView?: string;
}

/**
 * Publishes the module's on-screen snapshot to the host (module-host) or is a
 * no-op (standalone). Injected via BriefingRoomServices so the module stays
 * host-agnostic; the module-host implementation transports the snapshot to the
 * host over the same window-event bus the host uses for `vibe:module-action` /
 * `vibe:focus-panel`, and the host forwards it to its spaContextStore (gated).
 */
export interface SpaContextService {
  publish(snapshot: SpaContextSnapshot): void;
  clear(): void;
}

// ─── Aggregate ──────────────────────────────────────────────────────────

export interface BriefingRoomServices {
  host: HostMode;
  voice: VoiceService;
  persistence: PersistenceService;
  llm: LlmService;
  // V3 Voice GM (2026-06-02): the briefing-room-specific dispatcher.
  // Distinct from `llm` (which is the plain chat wrapper) because
  // V3 needs the tool-call contract for in-game actions.
  gmOrchestrator: GmOrchestratorService;
  mcp: McpRegistry;
  auth: AuthService;
  // See+control (2026-07-01): push an on-screen snapshot to the host so its
  // voice/Pi agents can SEE the Briefing Room. Optional so existing mock
  // service bundles (vitest) that omit it keep compiling.
  spaContext?: SpaContextService;
}
